import { FC, useContext, useState } from 'react';
import { UserContext } from 'context-providers/user-provider';
import { motion, type AnimationDefinition } from 'motion/react';

import {
  NavListHeading,
  NavList,
  NavLink,
  NavListItem,
  NavLinkTitle,
  NavLinkText,
  NavSection,
  NavDropdownScrollArea,
  NavDropdownStickyLink,
  NavLinkCopy,
  NavMediaTile,
  ExtraLinkList,
  listVariants,
  LabsTitle,
  Referral25Label,
} from 'components/home/TopNavigation/top-navigation.styled';
import { NavigationItem } from 'components/home/TopNavigation/top-navigation.types';
import TextButton from 'components/library/TextButton';
import Label from 'components/library/Label';
import Image from 'next/image';
import { trackEvent } from 'lib/analytics';
import { formatTrademarks } from 'lib/utils/format-trademarks';
import { useActiveMiniSale } from 'hooks/use-active-mini-sale';

interface NavSubListsProps {
  currentTab: NavigationItem;
  firstNavSubItemRef?: React.RefObject<HTMLAnchorElement>;
}

const NavSubLists: FC<NavSubListsProps> = ({
  currentTab,
  firstNavSubItemRef,
}) => {
  const handleClick = (href: string) => {
    trackEvent('Nav Submenu Click', {
      link: href,
    });
  };

  const { userState } = useContext(UserContext);
  const miniSale = useActiveMiniSale();
  // Sections share one spring, so the first one's completion stands in for all.
  const [isSettled, setIsSettled] = useState(false);
  const handleAnimationComplete = (definition: AnimationDefinition) => {
    if (definition === 'visible') setIsSettled(true);
  };
  // Redesign typography is scoped to the account menu for now.
  const isAccount = currentTab.id === 'account';
  return (
    <>
      <NavDropdownScrollArea $scrollable={isSettled}>
        {currentTab.subNavLists?.map((listItem, index) => {
          const headingId = `nav-section-${currentTab.id}-${index}`;

          return (
            <NavSection
              key={index}
              variants={listVariants}
              initial='initial'
              animate='visible'
              exit='exit'
              onAnimationComplete={
                index === 0 ? handleAnimationComplete : undefined
              }
            >
              {listItem.heading && (
                <NavListHeading id={headingId} $account={isAccount}>
                  {listItem.heading}
                  {listItem.headingLabel && (
                    <Label size='small'>{listItem.headingLabel}</Label>
                  )}
                </NavListHeading>
              )}
              <NavList aria-labelledby={headingId}>
                {listItem.list.map((item, index) => (
                  <NavListItem key={index}>
                    <NavLink
                      onClick={() => handleClick(item.href || '/')}
                      href={item.href || '/'}
                      ref={
                        index === 0 && firstNavSubItemRef
                          ? firstNavSubItemRef
                          : undefined
                      }
                    >
                      {item.media && item.mediaTile && (
                        <NavMediaTile>
                          <Image
                            width={67}
                            height={67}
                            src={item.media.src}
                            alt={item.media.alt}
                          />
                        </NavMediaTile>
                      )}
                      {item.media && !item.mediaTile && (
                        <Image
                          width={75}
                          height={75}
                          src={item.media.src}
                          alt={item.media.alt}
                          style={{ borderRadius: '16px' }}
                        />
                      )}
                      <NavLinkCopy $stacked={isAccount && item.textInPill}>
                        {item.textBeforeTitle ? (
                          <>
                            {item.miniSalePill && miniSale ? (
                              <Label size='small'>
                                {miniSale.saleBadgeText}
                              </Label>
                            ) : item.textInPill ? (
                              <Label size='small'>{item.text}</Label>
                            ) : (
                              <NavLinkText $account={isAccount}>
                                {formatTrademarks(item.text ?? '')}
                              </NavLinkText>
                            )}
                            <NavLinkTitle $account={isAccount}>
                              {item.title === 'Seed【 Labs 】' ? (
                                <>
                                  Seed<LabsTitle as='span'>Labs</LabsTitle>
                                </>
                              ) : (
                                item.title
                              )}
                            </NavLinkTitle>
                          </>
                        ) : (
                          <>
                            <NavLinkTitle $account={isAccount}>
                              {item.title === 'Seed【 Labs 】' ? (
                                <>
                                  Seed<LabsTitle as='span'>Labs</LabsTitle>
                                </>
                              ) : item.title === 'Refer' &&
                                userState?.experiments?.referral_nav ? (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    color: 'var(--fill-primary)',
                                  }}
                                >
                                  {item.title}
                                  <Referral25Label size='small'>
                                    $25
                                  </Referral25Label>
                                </div>
                              ) : (
                                item.title
                              )}
                            </NavLinkTitle>
                            {item.textInPill ? (
                              <Label size='small'>{item.text}</Label>
                            ) : (
                              <NavLinkText $account={isAccount}>
                                {formatTrademarks(item.text ?? '')}
                              </NavLinkText>
                            )}
                          </>
                        )}
                      </NavLinkCopy>
                    </NavLink>
                  </NavListItem>
                ))}
              </NavList>
            </NavSection>
          );
        })}
        {currentTab.extraLinks && (
          <motion.div
            key={currentTab.extraLinks.heading}
            variants={listVariants}
            initial='initial'
            animate='visible'
            exit='exit'
          >
            <NavListHeading $account={isAccount}>
              {currentTab.extraLinks.heading}
            </NavListHeading>
            <ExtraLinkList $account={isAccount}>
              {currentTab.extraLinks.list.map((link, index) => (
                <NavListItem key={index}>
                  <NavLink href={link.url || '/'}>{link.text}</NavLink>
                </NavListItem>
              ))}
            </ExtraLinkList>
          </motion.div>
        )}
        {currentTab.extraLink && (
          <motion.div
            key={currentTab.extraLink.text}
            variants={listVariants}
            initial='initial'
            animate='visible'
            exit='exit'
          >
            <TextButton
              color='snowwhite'
              align='left'
              label={currentTab.extraLink.text}
              url={currentTab.extraLink.url}
            />
          </motion.div>
        )}
      </NavDropdownScrollArea>
      {currentTab.extraLinkRight && (
        <NavDropdownStickyLink
          key={currentTab.extraLinkRight.text}
          variants={listVariants}
          initial='initial'
          animate='visible'
          exit='exit'
        >
          <TextButton
            color='snowwhite'
            align='left'
            label={currentTab.extraLinkRight.text}
            onClick={currentTab.extraLinkRight.action}
            url={currentTab.extraLinkRight.url}
            transform={currentTab.extraLinkRight.transform}
            underline={currentTab.extraLinkRight.underline}
            small={currentTab.extraLinkRight.small}
          />
        </NavDropdownStickyLink>
      )}
    </>
  );
};

export default NavSubLists;
